import { jsPDF } from "jspdf";

export interface CaseMetricPerson {
  name: string;
  meta?: string;
  isMain?: boolean;
}

export interface CaseMetricHistory {
  user: string;
  description: string;
  date: string;
}

export interface CaseMetricInput {
  title: string;
  signature: string;
  client: string;
  status: string;
  leadBy: string;
  court: string;
  createdDate: string;
  participants: CaseMetricPerson[];
  history: CaseMetricHistory[];
}

const fmtDate = (iso: string) =>
  iso
    ? new Date(iso).toLocaleDateString("pl-PL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

let fontRegularB64: string | null = null;
let fontBoldB64: string | null = null;

async function loadFontB64(url: string): Promise<string> {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export async function generateCaseMetricPdf(
  data: CaseMetricInput,
): Promise<Blob> {
  if (!fontRegularB64)
    fontRegularB64 = await loadFontB64("/fonts/LiberationSans-Regular.ttf");
  if (!fontBoldB64)
    fontBoldB64 = await loadFontB64("/fonts/LiberationSans-Bold.ttf");

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  doc.addFileToVFS("LiberationSans-Regular.ttf", fontRegularB64);
  doc.addFont("LiberationSans-Regular.ttf", "LiberationSans", "normal");
  doc.addFileToVFS("LiberationSans-Bold.ttf", fontBoldB64);
  doc.addFont("LiberationSans-Bold.ttf", "LiberationSans", "bold");

  const L = 14;
  const R = 196;
  const W = 182;
  let y = 16;

  const setF = (size: number, bold = false, r = 28, g = 27, b = 31) => {
    doc.setFont("LiberationSans", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(r, g, b);
  };

  const hline = () => {
    doc.setLineWidth(0.35);
    doc.setDrawColor(93, 73, 199);
    doc.line(L, y, R, y);
  };

  // ── Nagłówek dokumentu ─────────────────────────────────────────────
  setF(13, true, 93, 73, 199);
  doc.text("METRYKA TECZKI / SPRAWY", L, y);
  y += 4;
  hline();
  y += 7;

  // ── Tytuł ──────────────────────────────────────────────────────────
  setF(8, true, 100, 90, 120);
  doc.text("TYTUŁ:", L, y);
  setF(9.5, true, 28, 27, 31);
  const titleLines = doc.splitTextToSize(data.title || "—", W - 22);
  doc.text(titleLines, L + 22, y);
  y += titleLines.length * 5 + 3;

  const col1x = L;
  const col2x = L + 90;
  const labelW = 30;

  const drawMetaRow = (k1: string, v1: string, k2: string, v2: string) => {
    setF(7.5, true, 120, 110, 140);
    doc.text(k1, col1x, y);
    setF(9, false, 28, 27, 31);
    doc.text(v1, col1x + labelW, y);
    setF(7.5, true, 120, 110, 140);
    doc.text(k2, col2x, y);
    setF(9, false, 28, 27, 31);
    doc.text(v2, col2x + labelW, y);
    y += 5.5;
  };

  drawMetaRow("SYGNATURA:", data.signature || "—", "STATUS:", data.status || "—");
  drawMetaRow("WSZCZĘTA:", fmtDate(data.createdDate), "PROWADZĄCY:", data.leadBy || "—");

  setF(7.5, true, 120, 110, 140);
  doc.text("ORGAN:", col1x, y);
  setF(9, false, 28, 27, 31);
  const courtLines = doc.splitTextToSize(data.court || "—", W - labelW);
  doc.text(courtLines, col1x + labelW, y);
  y += courtLines.length * 5 + 1;

  setF(7.5, true, 120, 110, 140);
  doc.text("PODMIOT:", col1x, y);
  setF(9, false, 28, 27, 31);
  const clientLines = doc.splitTextToSize(data.client || "—", W - labelW);
  doc.text(clientLines, col1x + labelW, y);
  y += clientLines.length * 5 + 5;

  hline();
  y += 7;

  // ── Osoby powiązane ze sprawą ──────────────────────────────────────
  setF(10, true, 93, 73, 199);
  doc.text("OSOBY POWIĄZANE ZE SPRAWĄ", L, y);
  y += 6;

  if (data.participants.length === 0) {
    setF(9, false, 120, 110, 140);
    doc.text("Brak osób powiązanych ze sprawą.", L, y);
    y += 7;
  } else {
    data.participants.forEach((p) => {
      if (y > 270) {
        doc.addPage();
        y = 16;
      }
      setF(9, true, 28, 27, 31);
      doc.text(`${p.name}${p.isMain ? "  (główny)" : ""}`, L + 3, y);
      y += 4.5;
      if (p.meta) {
        setF(8, false, 120, 110, 140);
        doc.text(doc.splitTextToSize(p.meta, W - 6)[0], L + 3, y);
        y += 4;
      }
      y += 1.5;
    });
    y += 2;
  }

  hline();
  y += 7;

  // ── Historia teczki ────────────────────────────────────────────────
  setF(10, true, 93, 73, 199);
  doc.text("HISTORIA TECZKI", L, y);
  y += 6;

  if (data.history.length === 0) {
    setF(9, false, 120, 110, 140);
    doc.text("Brak historii teczki.", L, y);
    y += 6;
  } else {
    data.history.forEach((h) => {
      if (y > 268) {
        doc.addPage();
        y = 16;
      }
      setF(9, true, 28, 27, 31);
      doc.text(h.user || "—", L + 3, y);
      setF(8, false, 120, 110, 140);
      doc.text(h.date || "", R, y, { align: "right" });
      y += 4.5;
      if (h.description) {
        setF(8.5, false, 74, 67, 96);
        const descLines = doc.splitTextToSize(h.description, W - 6);
        doc.text(descLines, L + 3, y);
        y += descLines.length * 4 + 2;
      } else {
        y += 1.5;
      }
    });
  }

  y += 3;
  hline();
  y += 8;

  // ── Stopka ─────────────────────────────────────────────────────────
  setF(7.5, false, 130, 120, 140);
  doc.text(`Data wydruku: ${new Date().toLocaleDateString("pl-PL")}`, L, y);
  doc.text(".......................................", R, y, { align: "right" });
  y += 4.5;
  setF(7, false, 170, 165, 180);
  doc.text("podpis i pieczęć", R, y, { align: "right" });

  return doc.output("blob");
}
